// src/services/cookieService.ts

// Get all cookies as an object
export const getAllCookies = (): Record<string, string> => {
  return document.cookie
    .split(';')
    .reduce((cookies, cookie) => {
      const [name, value] = cookie.trim().split('=').map(decodeURIComponent);
      return Object.assign(cookies, { [name]: value });
    }, {});
};

// Get a specific cookie by name
export const getCookie = (name: string): string | null => {
  const cookies = getAllCookies();
  return cookies[name] || null;
};

// Set a cookie with options
export const setCookie = (
  name: string,
  value: string,
  options: {
    expires?: Date | number;
    path?: string;
    domain?: string;
    secure?: boolean;
    sameSite?: 'strict' | 'lax' | 'none';
    httpOnly?: boolean;
  } = {}
): void => {
  const {
    expires,
    path = '/',
    domain,
    secure = true, // Default to secure for financial application
    sameSite = 'strict', // Changed from 'lax' to 'strict' for better CSRF protection
    httpOnly = false, // HttpOnly can't be set from client-side JavaScript
  } = options;

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (expires) {
    if (typeof expires === 'number') {
      const days = expires;
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      cookieString += `; expires=${date.toUTCString()}`;
    } else {
      cookieString += `; expires=${expires.toUTCString()}`;
    }
  }

  if (path) cookieString += `; path=${path}`;
  if (domain) cookieString += `; domain=${domain}`;
  if (secure) cookieString += '; secure';
  if (httpOnly) cookieString += '; httponly'; // Note: This won't work from client-side, server must set it
  cookieString += `; samesite=${sameSite}`;

  // console.log('CookieService.setCookie - Setting cookie:', cookieString);
  document.cookie = cookieString;
  
  // Verify the cookie was set
  const verification = getCookie(name);
  // console.log('CookieService.setCookie - Verification result for', name, ':', verification?.substring(0, 50) || 'null');
};

// Delete a cookie by name
export const deleteCookie = (
  name: string,
  options: {
    path?: string;
    domain?: string;
  } = {}
): void => {
  const { path = '/', domain } = options;
  setCookie(name, '', {
    path,
    domain,
    expires: new Date(0), // Set expiration to epoch time to delete
  });
};

// Clear all cookies for this domain
export const clearAllCookies = (): void => {
  const cookies = getAllCookies();

  // Common paths where cookies might be set
  const paths = ['/', '/auth', '/api', '/app'];

  // Get current domain and parent domains
  const hostname = window.location.hostname;
  const domains = [
    hostname,
    `.${hostname}`,
    hostname.split('.').slice(1).join('.'), // parent domain
    `.${hostname.split('.').slice(1).join('.')}` // parent domain with dot
  ].filter(domain => domain && domain !== '.');

  for (const name in cookies) {
    // Try to delete with various path and domain combinations
    paths.forEach(path => {
      // Delete without domain specification
      deleteCookie(name, { path });

      // Delete with each domain variation
      domains.forEach(domain => {
        deleteCookie(name, { path, domain });
      });
    });

    // Also try with no path specified (default behavior)
    deleteCookie(name);
  }
};

// Force clear LocalStorage and SessionStorage as well
export const clearAllStorage = async (): Promise<void> => {
  console.log('Starting comprehensive storage clearing...');

  // Clear cookies first
  try {
    clearAllCookies();
    console.log('Cookies cleared');
  } catch (e) {
    console.error('Failed to clear cookies:', e);
  }

  // Clear localStorage
  try {
    localStorage.clear();
    console.log('localStorage cleared');
  } catch (e) {
    console.error('Failed to clear localStorage:', e);
  }

  // Clear sessionStorage
  try {
    sessionStorage.clear();
    console.log('sessionStorage cleared');
  } catch (e) {
    console.error('Failed to clear sessionStorage:', e);
  }

  // Clear IndexedDB
  try {
    if ('indexedDB' in window) {
      const databases = await indexedDB.databases();
      await Promise.all(
        databases.map(async (db) => {
          if (db.name) {
            return new Promise<void>((resolve, reject) => {
              const deleteReq = indexedDB.deleteDatabase(db.name!);
              deleteReq.onsuccess = () => resolve();
              deleteReq.onerror = () => reject(deleteReq.error);
            });
          }
        })
      );
      console.log('IndexedDB cleared');
    }
  } catch (e) {
    console.error('Failed to clear IndexedDB:', e);
  }

  // Clear Service Worker caches
  try {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('Service Worker caches cleared');
    }
  } catch (e) {
    console.error('Failed to clear Service Worker caches:', e);
  }

  // Clear any additional browser storage
  try {
    // Clear any potential WebSQL data (deprecated but might exist)
    if ('openDatabase' in window) {
      console.log('WebSQL detected but not cleared (deprecated)');
    }
  } catch (e) {
    console.error('Failed to check WebSQL:', e);
  }

  console.log('Storage clearing completed');
};